import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, MegaphoneIcon, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminMessages() {
  const [mechanics, setMechanics] = useState([]);
  const [messages, setMessages] = useState([]);
  const [target, setTarget] = useState(""); // "" = all mechanics (broadcast)
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const [u, m] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/messages", { params: target ? { mechanicId: target } : {} }),
      ]);
      setMechanics(u.data.filter((x) => x.role === "mechanic"));
      setMessages(m.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load messages");
    }
  };
  useEffect(() => { load(); }, [target]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const msg = text.trim();
    if (!msg) return;
    setBusy(true);
    try {
      const toIds = target ? [target] : [];
      const { data } = await api.post("/admin/messages", { toIds, message: msg });
      toast.success(`Sent to ${data.sent} recipient${data.sent === 1 ? "" : "s"}`);
      setText("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setBusy(false);
    }
  };

  const targetLabel = useMemo(() => {
    if (!target) return "All mechanics (broadcast)";
    const m = mechanics.find((x) => x.id === target);
    return m ? m.name : "Selected mechanic";
  }, [target, mechanics]);

  return (
    <div className="pb-4">
      <div className="px-6 pt-6 pb-3 flex items-center gap-2">
        <Link to="/app" data-testid="admin-msgs-back" className="text-[#9CA3AF] hover:text-white">
          <ArrowLeft size={20} />
        </Link>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#3CFF7E] font-display">ADMIN</div>
          <h1 className="font-display text-2xl font-black">Mechanic Messages</h1>
        </div>
      </div>

      <div className="px-6 space-y-3">
        <label className="block">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display mb-1">Send to</div>
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            data-testid="admin-msg-target"
            className="w-full h-11 bg-[#111827] border border-white/10 rounded-md px-3 text-sm text-white"
          >
            <option value="">All mechanics (broadcast)</option>
            {mechanics.map((m) => (
              <option key={m.id} value={m.id}>{m.name} · {m.email}</option>
            ))}
          </select>
        </label>

        <div
          ref={scrollRef}
          data-testid="admin-msgs-list"
          className="border border-white/10 rounded-md bg-[#0F1524] h-[45vh] overflow-y-auto p-3 space-y-2"
        >
          {messages.length === 0 ? (
            <div className="text-xs text-[#6B7280] text-center pt-4">No messages yet.</div>
          ) : messages.map((m) => (
            <MsgRow key={m.id} m={m} mine={m.fromRole === "admin"} />
          ))}
        </div>

        <div className="flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !busy) send(); }}
            data-testid="admin-msg-input"
            placeholder={`Message ${targetLabel}…`}
            className="flex-1 h-11 bg-[#111827] border border-white/10 rounded-md px-3 text-sm text-white"
          />
          <button
            onClick={send}
            disabled={busy || !text.trim()}
            data-testid="admin-msg-send"
            className="h-11 px-4 rounded-md bg-[#3CFF7E] text-[#0A0A0A] font-display font-bold uppercase text-[11px] tracking-wider flex items-center gap-1 disabled:opacity-50"
          >
            {target ? <Send size={14} /> : <MegaphoneIcon size={14} />}
            {busy ? "..." : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MsgRow({ m, mine }) {
  const time = new Date(m.created_at).toLocaleString("en-GB", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`admin-msg-${m.id}`}>
      <div className={`max-w-[80%] rounded-md p-2 ${mine ? "bg-[#3CFF7E]/10 border border-[#3CFF7E]/30" : "bg-[#111827] border border-white/10"}`}>
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#9CA3AF] font-display mb-0.5">
          {m.fromName || m.fromRole}
          {m.broadcast ? " · broadcast" : ""}
        </div>
        <div className="text-sm text-white whitespace-pre-wrap">{m.message}</div>
        <div className="text-[10px] text-[#6B7280] mt-1 text-right">{time}</div>
      </div>
    </div>
  );
}
